import groupBy from "lodash-es/groupBy.js";
import flatMap from "lodash-es/flatMap.js";
import { getGoliathImage } from "../goliath-media.js";
export const PROPERTY_KEY_MAP = {
    COLOR: "Farbe",
    STORAGE: "Speichergröße",
    SIZE: "Größe",
    MEMORY: "Speicher",
};
const mapName = (productAlternative, type) => {
    // Ensure productAlternative.offer exists
    if (!productAlternative.offer?.scopedProduct) {
        return "";
    }
    const scopedProduct = productAlternative.offer.scopedProduct;
    const fallbackName = scopedProduct.variantName || scopedProduct.name || "";
    switch (type) {
        case "SIZE":
            return scopedProduct.size || fallbackName;
        case "COLOR":
            return scopedProduct.color?.name || fallbackName;
        case "STORAGE":
            return scopedProduct.storage
                ? `${scopedProduct.storage.value} ${scopedProduct.storage.unit}`
                : fallbackName;
        default:
            return scopedProduct.variantName || "";
    }
};
/**
 * maps a `productAlternative` from offer request to needed values
 */
const mapProductAlternative = (productAlternative, type, detailViewPath, generateSlug = undefined) => {
    if (!productAlternative.offer)
        return undefined;
    const { product, scopedProduct } = productAlternative.offer;
    const path = generateSlug
        ? generateSlug(product?.slug || "")
        : detailViewPath + product?.slug;
    return {
        offer: productAlternative.offer,
        exactMatch: productAlternative.exactMatch || false,
        active: productAlternative.active || false,
        name: mapName(productAlternative, type),
        path,
        color: scopedProduct?.color || null,
        size: null,
        storage: null,
        teaserImage: getGoliathImage(productAlternative.offer),
    };
};
export const sortVariants = (variants, type) => {
    if (type === "SIZE") {
        const sortingArray = [
            "size xs",
            "size s",
            "size m",
            "size l",
            "size xl",
            "size xxl",
        ];
        variants.sort((a, b) => sortingArray.indexOf(a.name?.toLowerCase() || "") -
            sortingArray.indexOf(b.name?.toLowerCase() || ""));
        return variants;
    }
    return variants;
};
/**
 * maps a `ProductAlternativeSelectorGroup` from offer request to needed values
 */
const mapSelectorGroup = (selectorGroup, detailViewPath, generateSlug = undefined) => {
    if (!selectorGroup.alternatives || !selectorGroup?.key)
        return undefined;
    const variants = [];
    selectorGroup.alternatives.forEach((alternative) => {
        if (!alternative || !selectorGroup.key)
            return;
        const transformedAlternative = mapProductAlternative(alternative, selectorGroup.key, detailViewPath, generateSlug);
        if (!transformedAlternative)
            return;
        variants.push(transformedAlternative);
    });
    return {
        type: selectorGroup.key,
        displayName: PROPERTY_KEY_MAP[selectorGroup.key],
        items: sortVariants(variants, selectorGroup.key),
    };
};
/**
 * determine if a bundleItem is configurable by looking for non active alternatives
 */
const isConfigurable = (properties) => flatMap(properties, ({ items }) => items).filter(({ active }) => !active)
    .length > 0;
/**
 * returns the variantNames of the active variant e.g `['schwarz','256 GB']`
 */
const getActiveVariants = (properties) => flatMap(properties, (p) => p.items)
    .filter((a) => a.active)
    .map((a) => a.name);
const getTeaserImages = (properties) => {
    const activeTeaserImages = [];
    const otherTeaserImages = [];
    const mediaIds = new Set();
    properties.forEach((property) => {
        property.items.forEach((variant) => {
            if (variant.teaserImage && !mediaIds.has(variant.teaserImage.mediaId)) {
                mediaIds.add(variant.teaserImage.mediaId);
                if (variant.active) {
                    activeTeaserImages.push(variant.teaserImage);
                }
                else {
                    otherTeaserImages.push(variant.teaserImage);
                }
            }
        });
    });
    // Combine active teaser images with other teaser images
    return [...activeTeaserImages, ...otherTeaserImages];
};
/**
 * maps each bundleItem to needed values for component
 */
function mapToReducedBundleItems(bundleItem, detailViewPath, generateSlug = undefined) {
    if (!Array.isArray(bundleItem.properties))
        return undefined;
    const properties = [];
    bundleItem.properties.forEach((property) => {
        if (!property)
            return;
        const mappedGroup = mapSelectorGroup(property, detailViewPath, generateSlug);
        if (!mappedGroup)
            return;
        properties.push(mappedGroup);
    });
    return {
        family: bundleItem.family,
        position: bundleItem.position || 0,
        isConfigurable: isConfigurable(properties),
        activeVariants: getActiveVariants(properties),
        images: getTeaserImages(properties),
        properties,
        variants: {
            COLOR: properties.find((p) => p.type === "COLOR"),
            STORAGE: properties.find((p) => p.type === "STORAGE"),
            SIZE: properties.find((p) => p.type === "SIZE"),
            MEMORY: properties.find((p) => p.type === "MEMORY"),
        },
    };
}
/**
 * generate a name for merged bundleItems
 */
const itemNameWithCount = (name, count) => count > 1 ? `${count} x ${name}` : name;
/**
 * merges duplicate non-configurable bundleItems into a single item
 */
const maybeMergeBundleItems = (items) => {
    const mergeableItems = items.filter((item) => !item.isConfigurable);
    const nonMergeableItems = items.filter((item) => item.isConfigurable);
    const groups = groupBy(mergeableItems, "family.name");
    const groupsArray = Object.keys(groups).map((g) => groups[g]);
    const mergedItems = groupsArray.map((group) => ({
        ...group[0],
        isMerged: group.length > 1,
        family: {
            ...group[0].family,
            name: itemNameWithCount(group[0].family?.name || "", group.length),
        },
    }));
    return [...mergedItems, ...nonMergeableItems];
};
/**
 * Takes a Goliath Offer and transforms the productAlternatives into alternativeBundleItems for the OfferConfigurator component
 */
export const transformOfferAlternatives = (productAlternatives, detailViewPath = "/geraete/", generateSlug = undefined) => {
    const items = productAlternatives
        .map((item) => mapToReducedBundleItems(item, detailViewPath, generateSlug))
        .filter((item) => Boolean(item));
    return maybeMergeBundleItems(items);
};
export const getFirstOfferAlternatives = (productAlternatives, detailViewPath = "/geraete/", generateSlug = undefined) => {
    return transformOfferAlternatives(productAlternatives, detailViewPath, generateSlug)[0];
};
